import { Box, Button, Card, CardContent, Checkbox, FormControlLabel, InputLabel, OutlinedInput, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Select, { SingleValue } from 'react-select';
import { palette } from 'src/theme/style/palette';

import { currencyList } from 'src/components/shared/constants/currencyList';

const customStyles = {
    control: (base: any) => ({
        ...base,
        padding: '2px 6px',
        width: '220px',
        height: "53px",
        backgroundColor: "rgba(255, 255, 255, 0.2) !important",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        border: `1px solid ${palette.textGray}`,
        borderRadius: '4px',
        '&:hover': {
            borderColor: palette.blueLight,
        },
    }),
    menu: (base: any) => ({
        ...base,
        borderRadius: '4px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    }),
    option: (base: any, state: any) => ({
        ...base,
        backgroundColor: state.isSelected ? palette.success : base.backgroundColor,
        color: state.isSelected ? '#fff' : base.color,
        '&:hover': {
            backgroundColor: palette.successLight,
            color: '#000',
        },
    }),
    placeholder: (base: any) => ({
        ...base,
        color: palette.blue,
    }),
};

interface CurrencyOption {
    label: string;
    value: string;
}

export const ExchengeFilters = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const ownerFilter = searchParams.get('owner');
    const currencyFilter = searchParams.get('currency');
    const amountFilter = searchParams.get('amount');
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const [selectedCurrency, setSelectedCurrency] = useState<SingleValue<CurrencyOption>>({ label: 'Любая', value: 'ALL' });
    const [isCheckedOwner, setIsCheckedOwner] = useState(false);
    const [outAmount, setOutAmount] = useState<number>(0);
    const [filtersActive, setFiltersActive] = useState<boolean>(false);

    const clearFilters = () => {
        setFiltersActive(false);

        const newParams = new URLSearchParams();
        newParams.set('currency', 'ALL');
        newParams.set('amount', '0');
        newParams.set('owner', 'false');

        setSearchParams(newParams);
    };

    const handleChangeCurrency = (selectedOption: SingleValue<CurrencyOption>) => {
        setSelectedCurrency(selectedOption);
        const newParams = new URLSearchParams(searchParams.toString());
        newParams.set('currency', selectedOption?.value || 'ALL');
        setSearchParams(newParams);
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;
        setIsCheckedOwner(checked); // Обновляем состояние чекбокса

        const newParams = new URLSearchParams(searchParams.toString());
        newParams.set('owner', checked.toString());
        setSearchParams(newParams);
    };


    useEffect(() => {

        // Проверка, является ли значение числом
        const amount = isNaN(Number(amountFilter)) ? 0 : Number(amountFilter);

        // Назначаем значение в setOutAmount
        setOutAmount(amount);

        // проверка валют
        const isValidCurrency = currencyList.some(currency => currency.value.toUpperCase() === currencyFilter?.toUpperCase());

        if (isValidCurrency) {
            setSelectedCurrency(
                currencyList.find(
                    (currency) => currency.value.toUpperCase() === currencyFilter?.toUpperCase()
                ) || { label: 'Любая', value: 'ALL' }
            );
        } else {
            setSelectedCurrency({ label: 'Любая', value: 'ALL' });
            const newParams = new URLSearchParams(searchParams.toString());
            newParams.set('currency', 'ALL');
            setSearchParams(newParams);
        }

        // проверка фильтра владельца
        setIsCheckedOwner(ownerFilter === 'true');

        if (currencyFilter?.toUpperCase() === 'ALL' && amount < 1 && ownerFilter != 'true') {
            setFiltersActive(false);
        } else {
            setFiltersActive(true);
        }

    }, [searchParams]);

    return (
        <>
            <Card sx={{ mb: 1, position: 'relative' }}>
                <CardContent>
                    <Typography variant='h6' mb={1}>Фильтры</Typography>
                    <Box display="flex" flexWrap="wrap" alignItems='end'>
                        <Box sx={{ display: "flex", flexDirection: "column", mr: 1, mb: 1, width: { xs: "60%", md: "200px" } }}>
                            <InputLabel sx={{ mb: 0.5 }}>Сумма от</InputLabel>
                            <OutlinedInput
                                id="amount-input"
                                size="small" // 👉 Делаем компактный размер
                                value={outAmount}
                                inputProps={{
                                    inputMode: "numeric",
                                    pattern: "[0-9]*",
                                }}
                                onChange={(e) => {
                                    const target = e.target as HTMLInputElement;
                                    const sanitizedValue = target.value.replace(/[^0-9]/g, "");

                                    setOutAmount(sanitizedValue ? Number(sanitizedValue) : 0); // Обновление состояния

                                    if (timeoutRef.current) clearTimeout(timeoutRef.current);

                                    timeoutRef.current = setTimeout(() => {
                                        const newParams = new URLSearchParams(searchParams.toString());
                                        newParams.set("amount", sanitizedValue);
                                        setSearchParams(newParams);
                                    }, 1000);

                                }}
                            />
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "column", mr: 2, mb: 1, width: { xs: "60%", md: "200px" } }}>
                            <InputLabel sx={{ mb: 0.5 }}>Валюта получения</InputLabel>
                            <Select<CurrencyOption>
                                value={selectedCurrency}
                                onChange={handleChangeCurrency}
                                options={currencyList}
                                getOptionLabel={(e: CurrencyOption) => e.label}
                                getOptionValue={(e: CurrencyOption) => e.value}
                                placeholder="Выберите валюту"
                                isSearchable={true}
                                styles={{
                                    ...customStyles,
                                    menuPortal: (base: any) => ({
                                        ...base,
                                        zIndex: 9999,

                                    }),
                                }}
                                menuPortalTarget={document.body}
                            />
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "column", ml: 2, mr: 1, mb: 1, width: { xs: "100%", sm: "auto" } }}>
                            <InputLabel sx={{ mb: 1.5 }}>Фильтр</InputLabel> {/* Лейбл над чекбоксом */}
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isCheckedOwner}
                                        onChange={handleChange}
                                        color="primary"
                                    />
                                }
                                label="Показать мои"
                            />
                        </Box>
                        {filtersActive &&
                            <Box sx={{ mr: 1, mb: { xs: 2, md: 1 }, width: { xs: '100%', md: 'auto' } }}>
                                <Button onClick={() => clearFilters()}
                                    color='error'
                                    size='small'
                                    sx={{
                                        mb: 1
                                        //     background: "red", "&:hover": {
                                        //         background: palette.errorLight,
                                        //         "&>svg": {
                                        //             color: `${palette.error} !important`,
                                        //         }
                                        //     }
                                    }}
                                >
                                    {/* <CloseRoundedIcon sx={{ color: '#fff !important' }} /> */}
                                    Очистить
                                </Button>
                            </Box>
                        }
                    </Box>
                </CardContent>
            </Card>
        </>
    );
};